import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Mail, Building2, Users, Loader } from 'lucide-react';
import type { CurrentPage } from '../App';
import type { User } from '@supabase/supabase-js';
import type { TeamInvitationWithDetails } from '../types/team';
import { getInvitationByToken, acceptInvitation } from '../services/teamService';

interface InviteAcceptPageProps {
  onNavigate: (page: CurrentPage) => void;
  currentUser: User | null;
  invitationToken?: string;
}

const InviteAcceptPage: React.FC<InviteAcceptPageProps> = ({
  onNavigate,
  currentUser,
  invitationToken
}) => {
  const [invitation, setInvitation] = useState<TeamInvitationWithDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (invitationToken) {
      validateInvitation();
    } else {
      setError('No invitation token provided');
      setIsLoading(false);
    }
  }, [invitationToken]);

  const validateInvitation = async () => {
    try {
      setIsLoading(true);
      const inviteData = await getInvitationByToken(invitationToken!);
      
      if (!inviteData) {
        setError('This invitation is invalid or has expired');
      } else {
        setInvitation(inviteData);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load invitation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptInvitation = async () => {
    if (!currentUser) {
      // Redirect to signup with invitation token
      onNavigate({ page: 'auth', inviteToken: invitationToken });
      return;
    }

    try {
      setIsAccepting(true);
      setError(null);
      
      await acceptInvitation(invitationToken!, currentUser.id);
      setSuccess(true);

      // Redirect to home after 2 seconds
      setTimeout(() => {
        onNavigate({ page: 'start' });
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to accept invitation');
    } finally {
      setIsAccepting(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-green-600 border-t-transparent mb-4"></div>
          <p className="text-slate-600 font-medium text-lg">Validating invitation...</p>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl border border-green-200 p-8 sm:p-12 max-w-lg text-center">
          <div className="inline-flex p-4 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full mb-6">
            <CheckCircle className="text-green-600" size={64} />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-3">
            Welcome to the Team! 🎉
          </h1>
          <p className="text-lg text-slate-600 mb-6">
            You've successfully joined <strong>{invitation?.organization_name}</strong>
          </p>
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
            <p className="text-sm text-green-800">
              Redirecting you to the app...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !invitation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl border border-red-200 p-8 sm:p-12 max-w-lg text-center">
          <div className="inline-flex p-4 bg-gradient-to-r from-red-100 to-pink-100 rounded-full mb-6">
            <XCircle className="text-red-600" size={64} />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-3">
            Invalid Invitation
          </h1>
          <p className="text-lg text-slate-600 mb-6">
            {error || 'This invitation link is no longer valid'}
          </p>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">
              This could mean the invitation has expired (7 days), been revoked, or already been used.
            </p>
            <button
              onClick={() => onNavigate({ page: 'auth' })}
              className="w-full px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all shadow-md"
            >
              Go to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Invitation details view
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl border border-green-200 p-8 sm:p-12 max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex p-4 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full mb-4">
            <Mail className="text-green-600" size={48} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-green-900 via-emerald-900 to-teal-900 bg-clip-text text-transparent mb-2">
            You're Invited!
          </h1>
          <p className="text-lg text-slate-600">
            Join your team on NOOR
          </p>
        </div>

        {/* Invitation Details */}
        <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl p-6 border border-slate-200 mb-6">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Building2 className="text-green-600 flex-shrink-0 mt-1" size={24} />
              <div>
                <p className="text-sm font-semibold text-slate-600">Organization</p>
                <p className="text-lg font-bold text-slate-900">{invitation.organization_name}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Users className="text-green-600 flex-shrink-0 mt-1" size={24} />
              <div>
                <p className="text-sm font-semibold text-slate-600">Invited by</p>
                <p className="text-lg font-bold text-slate-900">{invitation.invited_by_name}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Mail className="text-green-600 flex-shrink-0 mt-1" size={24} />
              <div>
                <p className="text-sm font-semibold text-slate-600">Your email</p>
                <p className="text-lg font-bold text-slate-900">{invitation.invited_email}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200 mb-6">
          <h3 className="font-bold text-green-900 mb-3 flex items-center gap-2">
            <CheckCircle size={20} />
            What you'll get:
          </h3>
          <ul className="space-y-2 text-sm text-green-800">
            <li className="flex items-start gap-2">
              <span className="text-green-600">•</span>
              <span><strong>Free access</strong> to all NOOR features</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600">•</span>
              <span><strong>Collaborate</strong> with your team</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600">•</span>
              <span><strong>Share data</strong> across your organization</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600">•</span>
              <span><strong>Your own</strong> login and credentials</span>
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-start gap-2">
            <XCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="space-y-3">
          {currentUser ? (
            <>
              {/* User is logged in - show accept button */}
              <button
                onClick={handleAcceptInvitation}
                disabled={isAccepting}
                className="w-full px-6 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-bold text-lg hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                {isAccepting ? (
                  <>
                    <Loader className="animate-spin" size={24} />
                    Accepting...
                  </>
                ) : (
                  <>
                    <CheckCircle size={24} />
                    Accept Invitation
                  </>
                )}
              </button>
              <p className="text-xs text-center text-slate-500">
                You're logged in as <strong>{currentUser.email}</strong>
              </p>
            </>
          ) : (
            <>
              {/* User is not logged in - show signup/login options */}
              <button
                onClick={() => onNavigate({ page: 'auth', inviteToken: invitationToken })}
                className="w-full px-6 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-bold text-lg hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl"
              >
                Sign Up & Accept
              </button>
              <button
                onClick={() => onNavigate({ page: 'auth', inviteToken: invitationToken })}
                className="w-full px-6 py-3 bg-white text-slate-700 border-2 border-slate-300 rounded-xl font-semibold hover:bg-slate-50 transition-all"
              >
                Already have an account? Sign In
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 pt-6 border-t border-slate-200">
          <p className="text-xs text-center text-slate-500">
            By accepting this invitation, you'll be added to {invitation.organization_name}'s team
            with free access under their plan.
          </p>
        </div>
      </div>
    </div>
  );
};

export default InviteAcceptPage;
